import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SafeIcon from '../common/SafeIcon';

export default function CookiePolicy() {
  const lastUpdated = "December 1, 2025";
  
  const tocItems = [
    { id: 'what-are-cookies', title: 'What Are Cookies?' },
    { id: 'how-we-use', title: 'How RepUpgrade Uses Cookies' },
    { id: 'types-used', title: 'Types of Cookies We Use' },
    { id: 'your-choices', title: 'Your Cookie Choices' },
    { id: 'updates', title: 'Updates to Our Cookie Policy' },
    { id: 'contact', title: 'Contact for More Information' }
  ];

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle smooth scroll for TOC to avoid HashRouter conflicts and account for fixed navbar
  const handleScrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 80; // height of fixed navbar (h-20 = 80px)
      const y = element.getBoundingClientRect().top + window.scrollY - navbarHeight - 32; // 32px extra padding
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-dc-bg text-dc-text">
      <Navbar />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Legal Hero */}
          <div className="mb-16 text-center">
            <div className="inline-block bg-dc-teal-deep/5 text-dc-teal-signal text-xs font-michroma px-4 py-2 rounded-full mb-6 border border-dc-teal-signal/20">
              LEGAL
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Cookie Policy</h1>
            <p className="text-lg text-dc-text/70 max-w-2xl mx-auto mb-4">
              At RepUpgrade, we use cookies and similar technologies to enhance your browsing experience, analyze site traffic, and personalize content. This policy explains what cookies are, how we use them, and your choices regarding their use.
            </p>
            <p className="text-sm text-dc-text/50">
              By continuing to use our website, you agree to the use of cookies as described herein.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-soft mb-16">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <SafeIcon name="List" className="w-5 h-5 text-dc-teal-signal" />
              Table of Contents
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {tocItems.map((item, index) => (
                <a 
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={(e) => handleScrollToSection(e, item.id)}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-dc-bg transition-colors group cursor-pointer"
                >
                  <span className="text-dc-teal-signal font-michroma text-sm mt-1 group-hover:text-dc-teal-deep">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-dc-text group-hover:text-dc-teal-signal font-medium">
                    {item.title}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* Cookie Policy Sections */}
          <div className="space-y-16">
            <section id="what-are-cookies">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                1. What Are Cookies?
              </h2>
              <div className="space-y-6 text-dc-text/80 leading-relaxed">
                <p>
                  Cookies are small text files stored on your device (computer, tablet, or mobile phone) when you visit websites. They are widely used to make websites work more efficiently, as well as to provide information to the owners of the site. They allow websites to remember information about your visit, like your preferred language or other settings, which can make your next visit easier and the site more useful to you.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-6 mt-6">
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="font-bold text-dc-text mb-2 text-lg flex items-center gap-2">
                      <SafeIcon name="Server" className="w-5 h-5 text-dc-teal-signal" /> First-Party Cookies
                    </h3>
                    <p className="text-sm">These cookies are set by the website you are visiting (e.g., RepUpgrade.com). They are generally used to allow the website to function, remember your preferences, or compile anonymous statistics about your usage of the site.</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="font-bold text-dc-text mb-2 text-lg flex items-center gap-2">
                      <SafeIcon name="Clock" className="w-5 h-5 text-dc-teal-signal" /> Session Cookies
                    </h3>
                    <p className="text-sm">Session cookies are temporary and are erased when you close your web browser. They are typically used to remember information about your activities on the website during a single visit, such as items in a shopping cart.</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="font-bold text-dc-text mb-2 text-lg flex items-center gap-2">
                      <SafeIcon name="Globe" className="w-5 h-5 text-dc-teal-signal" /> Third-Party Cookies
                    </h3>
                    <p className="text-sm">Third-party cookies are set by a domain other than the one you are visiting (e.g., ad networks or analytics services). They are often used for tracking user behavior across different websites for advertising or analytics purposes.</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="font-bold text-dc-text mb-2 text-lg flex items-center gap-2">
                      <SafeIcon name="Save" className="w-5 h-5 text-dc-teal-signal" /> Persistent Cookies
                    </h3>
                    <p className="text-sm">Persistent cookies remain on your device for a set period specified in the cookie's parameters, or until you manually delete them. They are used to remember your preferences or login information over multiple sessions.</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="how-we-use">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                2. How RepUpgrade Uses Cookies
              </h2>
              <div className="space-y-6 text-dc-text/80 leading-relaxed">
                <p>
                  At RepUpgrade, we use cookies to enhance your experience, analyze site traffic, and deliver personalized content. Below is a detailed breakdown of the types of cookies we employ and their specific functions.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-5 rounded-xl bg-[#F7F8F7] border border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                      <SafeIcon name="Lock" className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-dc-text mb-1">Essential Cookies</h3>
                      <p className="text-sm">These cookies are crucial for the basic functionality of our website and enable core features like security, network management, and accessibility. You cannot opt out of these cookies.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-5 rounded-xl bg-[#F7F8F7] border border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                      <SafeIcon name="BarChart2" className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-dc-text mb-1">Performance & Analytics Cookies</h3>
                      <p className="text-sm">These cookies collect information about how you use our website, such as which pages you visit and if you encounter any errors. They help us improve how the website works.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-5 rounded-xl bg-[#F7F8F7] border border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                      <SafeIcon name="Settings" className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-dc-text mb-1">Functionality Cookies</h3>
                      <p className="text-sm">These cookies allow our website to remember choices you make (like your username, language, or region) and provide enhanced, more personal features to you.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-5 rounded-xl bg-[#F7F8F7] border border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                      <SafeIcon name="Target" className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-dc-text mb-1">Advertising & Marketing Cookies</h3>
                      <p className="text-sm">These cookies are used to deliver advertisements more relevant to you and your interests. They are also used to limit the number of times you see an advertisement and help measure the effectiveness of advertising campaigns.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section id="types-used">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                3. Types of Cookies We Use
              </h2>
              <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-dc-teal-deep/5 text-dc-teal-deep text-sm">
                      <th className="py-4 px-6 font-bold border-b border-gray-200">Cookie Name</th>
                      <th className="py-4 px-6 font-bold border-b border-gray-200">Purpose</th>
                      <th className="py-4 px-6 font-bold border-b border-gray-200">Type</th>
                      <th className="py-4 px-6 font-bold border-b border-gray-200">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-dc-text/80">
                    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 font-mono font-bold text-dc-text">session_id</td>
                      <td className="py-4 px-6">Essential for user authentication and session management.</td>
                      <td className="py-4 px-6">Session (First-party)</td>
                      <td className="py-4 px-6 text-dc-teal-signal font-medium">Browser close</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 font-mono font-bold text-dc-text">preferences</td>
                      <td className="py-4 px-6">Remembers user preferences like language or theme settings.</td>
                      <td className="py-4 px-6">Persistent (First-party)</td>
                      <td className="py-4 px-6 text-dc-teal-signal font-medium">1 year</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 font-mono font-bold text-dc-text">analytics_id</td>
                      <td className="py-4 px-6">Collects anonymous data on how users interact with the site to improve user experience.</td>
                      <td className="py-4 px-6">Persistent (Third-party)</td>
                      <td className="py-4 px-6 text-dc-teal-signal font-medium">2 years</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 font-mono font-bold text-dc-text">csrftoken</td>
                      <td className="py-4 px-6">Security cookie to prevent Cross-Site Request Forgery attacks.</td>
                      <td className="py-4 px-6">Session (First-party)</td>
                      <td className="py-4 px-6 text-dc-teal-signal font-medium">Browser close</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section id="your-choices">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                4. Your Cookie Choices
              </h2>
              <div className="space-y-6 text-dc-text/80 leading-relaxed">
                <p>
                  At RepUpgrade, we believe in transparency and empowering you to control your online experience. This section explains how you can manage your cookie preferences.
                </p>
                <p>
                  Cookies are small text files stored on your device by websites you visit. They help us improve your browsing experience, analyze site traffic, and personalize content. You have the right to accept, decline, or delete cookies at any time.
                </p>
                
                <h3 className="font-bold text-dc-text mb-3 text-lg mt-8">How to Manage Cookies in Your Browser</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-dc-teal-signal/50 hover:shadow-sm transition-all group">
                    <span className="font-bold text-dc-text group-hover:text-dc-teal-signal transition-colors">Google Chrome</span>
                    <SafeIcon name="ExternalLink" className="w-4 h-4 text-gray-400 group-hover:text-dc-teal-signal" />
                  </a>
                  <a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-dc-teal-signal/50 hover:shadow-sm transition-all group">
                    <span className="font-bold text-dc-text group-hover:text-dc-teal-signal transition-colors">Mozilla Firefox</span>
                    <SafeIcon name="ExternalLink" className="w-4 h-4 text-gray-400 group-hover:text-dc-teal-signal" />
                  </a>
                  <a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-dc-teal-signal/50 hover:shadow-sm transition-all group">
                    <span className="font-bold text-dc-text group-hover:text-dc-teal-signal transition-colors">Apple Safari</span>
                    <SafeIcon name="ExternalLink" className="w-4 h-4 text-gray-400 group-hover:text-dc-teal-signal" />
                  </a>
                  <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-dc-teal-signal/50 hover:shadow-sm transition-all group">
                    <span className="font-bold text-dc-text group-hover:text-dc-teal-signal transition-colors">Microsoft Edge</span>
                    <SafeIcon name="ExternalLink" className="w-4 h-4 text-gray-400 group-hover:text-dc-teal-signal" />
                  </a>
                </div>

                <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-100 mt-6">
                  <h3 className="font-bold text-yellow-800 mb-2 flex items-center gap-2">
                    <SafeIcon name="AlertCircle" className="w-5 h-5" />
                    Manage Your Preferences
                  </h3>
                  <p className="text-yellow-900/80 text-sm">
                    Please note that blocking some types of cookies may impact your experience on our site and the services we are able to offer. By continuing to use our site, you consent to the use of cookies as described in our policy, unless you have adjusted your browser settings.
                  </p>
                </div>
              </div>
            </section>

            <section id="updates">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                5. Updates to Our Cookie Policy
              </h2>
              <div className="space-y-4 text-dc-text/80 leading-relaxed">
                <p className="font-medium text-dc-text">
                  Last Updated: {lastUpdated}
                </p>
                <p>
                  RepUpgrade may update this Cookie Policy periodically to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage you to review this policy regularly to stay informed about how we are using cookies. Any changes will be posted on this page with an updated 'Last Updated' date.
                </p>
              </div>
            </section>

            <section id="contact">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                6. Contact for More Information
              </h2>
              <div className="space-y-6 text-dc-text/80 leading-relaxed">
                <p>
                  If you have any questions, concerns, or require further clarification about our Cookie Policy, please do not hesitate to reach out to us. Our team is here to assist you.
                </p>
                
                <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm inline-block w-full md:w-auto">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-dc-teal-deep flex items-center justify-center">
                      <SafeIcon name="Mail" className="text-dc-gold w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-dc-text text-lg">Email Address</h3>
                  </div>
                  <a href="mailto:privacy@repupgrade.com" className="text-dc-teal-signal hover:text-dc-teal-deep font-medium transition-colors text-lg">
                    privacy@repupgrade.com
                  </a>
                </div>
                
                <div className="mt-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center gap-2 bg-dc-orange hover:bg-dc-orange-hover text-white font-semibold py-4 px-8 rounded-xl shadow-lg transition-all hover:-translate-y-1"
                  >
                    Go to Contact Page
                    <SafeIcon name="ArrowRight" className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </section>
          </div>

          {/* Back to Home CTA */}
          <div className="mt-20 pt-10 border-t border-gray-200">
            <div className="bg-dc-bg rounded-3xl p-10 text-center">
              <h2 className="text-2xl font-bold text-dc-text mb-4">Return to RepUpgrade</h2>
              <p className="text-dc-text/70 mb-8 max-w-2xl mx-auto">
                Back to building trust, content, and clear next steps for your local business.
              </p>
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-dc-teal-signal/30 text-dc-text font-semibold py-3 px-8 rounded-xl shadow-sm transition-all"
              >
                <SafeIcon name="ArrowLeft" className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}